import React, { useState } from 'react';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import './BlogManagement.css';

const BlogManagement = () => {
  const [activeTab, setActiveTab] = useState('categories');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  // Category Form State
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    slug: ''
  });

  // Post Form State
  const [postForm, setPostForm] = useState({
    title: '',
    slug: '',
    category: '',
    featuredImage: null,
    featuredImagePreview: '',
    excerpt: '',
    status: 'draft'
  });

  // Quill Editor Setup
  const { quill, quillRef } = useQuill({
    modules: {
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link', 'image', 'video'],
        ['blockquote', 'code-block'],
        ['clean']
      ]
    },
    theme: 'snow',
    placeholder: 'Write your blog content here...'
  });

  // Sample Categories Data
  const [categories, setCategories] = useState([
    { id: 1, name: 'Technology', slug: 'technology', description: 'Tech related posts', postCount: 12 },
    { id: 2, name: 'Lifestyle', slug: 'lifestyle', description: 'Life and style articles', postCount: 8 },
    { id: 3, name: 'Business', slug: 'business', description: 'Business insights', postCount: 15 }
  ]);

  // Sample Posts Data
  const [posts, setPosts] = useState([
    {
      id: 1,
      title: 'Getting Started with React',
      slug: 'getting-started-react',
      category: 'Technology',
      author: 'John Doe',
      status: 'published',
      date: '2026-02-01',
      views: 1234,
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=250&fit=crop'
    },
    {
      id: 2,
      title: '10 Tips for Productive Morning',
      slug: '10-tips-productive-morning',
      category: 'Lifestyle',
      author: 'Jane Smith',
      status: 'published',
      date: '2026-01-28',
      views: 892,
      image: 'https://images.unsplash.com/photo-1495364141860-b0d03eccd065?w=400&h=250&fit=crop'
    },
    {
      id: 3,
      title: 'Business Growth Strategies',
      slug: 'business-growth-strategies',
      category: 'Business',
      author: 'Mike Johnson',
      status: 'draft',
      date: '2026-01-25',
      views: 0,
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop'
    }
  ]);

  // Category Handlers
  const handleCategoryFormChange = (e) => {
    const { name, value } = e.target;
    setCategoryForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'name' && { slug: value.toLowerCase().replace(/\s+/g, '-') })
    }));
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', description: '', slug: '' });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description,
      slug: category.slug
    });
    setShowCategoryModal(true);
  };

  const handleSaveCategory = () => {
    if (editingCategory) {
      setCategories(categories.map(cat =>
        cat.id === editingCategory.id
          ? { ...cat, ...categoryForm }
          : cat
      ));
    } else {
      setCategories([...categories, {
        id: Date.now(),
        ...categoryForm,
        postCount: 0
      }]);
    }
    setShowCategoryModal(false);
    setCategoryForm({ name: '', description: '', slug: '' });
  };

  const handleDeleteCategory = (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      setCategories(categories.filter(cat => cat.id !== id));
    }
  };

  // Post Handlers
  const handlePostFormChange = (e) => {
    const { name, value } = e.target;
    setPostForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'title' && { slug: value.toLowerCase().replace(/\s+/g, '-') })
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPostForm(prev => ({
          ...prev,
          featuredImage: file,
          featuredImagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreatePost = () => {
    setEditingPost(null);
    setPostForm({
      title: '',
      slug: '',
      category: '',
      featuredImage: null,
      featuredImagePreview: '',
      excerpt: '',
      status: 'draft'
    });
    if (quill) {
      quill.setText('');
    }
    setShowPostModal(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      slug: post.slug,
      category: post.category,
      featuredImage: null,
      featuredImagePreview: post.image,
      excerpt: 'Sample excerpt for editing...',
      status: post.status
    });
    setShowPostModal(true);
  };

  const handleSavePost = () => {
    const content = quill ? quill.root.innerHTML : '';
    
    if (editingPost) {
      setPosts(posts.map(post =>
        post.id === editingPost.id
          ? { ...post, ...postForm, image: postForm.featuredImagePreview }
          : post
      ));
    } else {
      setPosts([...posts, {
        id: Date.now(),
        ...postForm,
        image: postForm.featuredImagePreview,
        author: 'Current User',
        date: new Date().toISOString().split('T')[0],
        views: 0
      }]);
    }
    setShowPostModal(false);
  };

  const handleDeletePost = (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      setPosts(posts.filter(post => post.id !== id));
    }
  };

  return (
    <div className="create-blogs-container">
      {/* Header */}
      <div className="create-blogs-header">
        <div>
          <h1 className="create-blogs-title">Blog Management</h1>
          <p className="create-blogs-subtitle">Manage your blog categories and posts</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="create-blogs-tabs">
        <button
          className={`create-blogs-tab ${activeTab === 'categories' ? 'create-blogs-tab-active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          Blog Categories
        </button>
        <button
          className={`create-blogs-tab ${activeTab === 'posts' ? 'create-blogs-tab-active' : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Blog Posts
        </button>
      </div>

      {/* Content */}
      <div className="create-blogs-content">
        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="create-blogs-section">
            <div className="create-blogs-section-header">
              <h2>All Categories</h2>
              <button className="create-blogs-btn-primary" onClick={handleCreateCategory}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Add Category
              </button>
            </div>

            <div className="create-blogs-table-wrapper">
              <table className="create-blogs-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Description</th>
                    <th>Posts</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(category => (
                    <tr key={category.id}>
                      <td>
                        <span className="create-blogs-category-name">{category.name}</span>
                      </td>
                      <td>
                        <code className="create-blogs-slug">{category.slug}</code>
                      </td>
                      <td>{category.description}</td>
                      <td>
                        <span className="create-blogs-badge">{category.postCount}</span>
                      </td>
                      <td>
                        <div className="create-blogs-actions">
                          <button
                            className="create-blogs-action-btn create-blogs-action-edit"
                            onClick={() => handleEditCategory(category)}
                            title="Edit"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            className="create-blogs-action-btn create-blogs-action-delete"
                            onClick={() => handleDeleteCategory(category.id)}
                            title="Delete"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div className="create-blogs-section">
            <div className="create-blogs-section-header">
              <h2>All Posts</h2>
              <button className="create-blogs-btn-primary" onClick={handleCreatePost}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Create Post
              </button>
            </div>

            <div className="create-blogs-posts-grid">
              {posts.map(post => (
                <div key={post.id} className="create-blogs-post-card">
                  <div className="create-blogs-post-image">
                    <img src={post.image} alt={post.title} />
                    <span className={`create-blogs-post-status create-blogs-status-${post.status}`}>
                      {post.status}
                    </span>
                  </div>
                  <div className="create-blogs-post-content">
                    <span className="create-blogs-post-category">{post.category}</span>
                    <h3 className="create-blogs-post-title">{post.title}</h3>
                    <div className="create-blogs-post-meta">
                      <span>👤 {post.author}</span>
                      <span>📅 {post.date}</span>
                      <span>👁️ {post.views}</span>
                    </div>
                    <div className="create-blogs-post-actions">
                      <button
                        className="create-blogs-btn-secondary"
                        onClick={() => handleEditPost(post)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        className="create-blogs-btn-danger"
                        onClick={() => handleDeletePost(post.id)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="create-blogs-modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="create-blogs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="create-blogs-modal-header">
              <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
              <button
                className="create-blogs-modal-close"
                onClick={() => setShowCategoryModal(false)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="create-blogs-modal-body">
              <div className="create-blogs-form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  name="name"
                  value={categoryForm.name}
                  onChange={handleCategoryFormChange}
                  placeholder="Enter category name"
                  className="create-blogs-input"
                />
              </div>
              <div className="create-blogs-form-group">
                <label>Slug *</label>
                <input
                  type="text"
                  name="slug"
                  value={categoryForm.slug}
                  onChange={handleCategoryFormChange}
                  placeholder="category-slug"
                  className="create-blogs-input"
                />
              </div>
              <div className="create-blogs-form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={categoryForm.description}
                  onChange={handleCategoryFormChange}
                  placeholder="Enter category description"
                  className="create-blogs-textarea"
                  rows="3"
                />
              </div>
            </div>
            <div className="create-blogs-modal-footer">
              <button
                className="create-blogs-btn-secondary"
                onClick={() => setShowCategoryModal(false)}
              >
                Cancel
              </button>
              <button
                className="create-blogs-btn-primary"
                onClick={handleSaveCategory}
                disabled={!categoryForm.name || !categoryForm.slug}
              >
                {editingCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post Modal */}
      {showPostModal && (
        <div className="create-blogs-modal-overlay" onClick={() => setShowPostModal(false)}>
          <div className="create-blogs-modal create-blogs-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="create-blogs-modal-header">
              <h3>{editingPost ? 'Edit Post' : 'Create New Post'}</h3>
              <button
                className="create-blogs-modal-close"
                onClick={() => setShowPostModal(false)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="create-blogs-modal-body create-blogs-modal-scrollable">
              <div className="create-blogs-form-row">
                <div className="create-blogs-form-group create-blogs-form-group-full">
                  <label>Post Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={postForm.title}
                    onChange={handlePostFormChange}
                    placeholder="Enter post title"
                    className="create-blogs-input"
                  />
                </div>
              </div>

              <div className="create-blogs-form-row">
                <div className="create-blogs-form-group">
                  <label>Slug *</label>
                  <input
                    type="text"
                    name="slug"
                    value={postForm.slug}
                    onChange={handlePostFormChange}
                    placeholder="post-slug"
                    className="create-blogs-input"
                  />
                </div>
                <div className="create-blogs-form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={postForm.category}
                    onChange={handlePostFormChange}
                    className="create-blogs-select"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="create-blogs-form-group">
                <label>Featured Image</label>
                <div className="create-blogs-image-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="create-blogs-file-input"
                    id="featured-image"
                  />
                  <label htmlFor="featured-image" className="create-blogs-file-label">
                    {postForm.featuredImagePreview ? (
                      <img src={postForm.featuredImagePreview} alt="Preview" />
                    ) : (
                      <div className="create-blogs-upload-placeholder">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Click to upload image</span>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div className="create-blogs-form-group">
                <label>Excerpt</label>
                <textarea
                  name="excerpt"
                  value={postForm.excerpt}
                  onChange={handlePostFormChange}
                  placeholder="Enter a short excerpt"
                  className="create-blogs-textarea"
                  rows="3"
                />
              </div>

              <div className="create-blogs-form-group">
                <label>Content *</label>
                <div className="create-blogs-editor-wrapper">
                  <div ref={quillRef} />
                </div>
              </div>

              <div className="create-blogs-form-group">
                <label>Status</label>
                <div className="create-blogs-radio-group">
                  <label className="create-blogs-radio-label">
                    <input
                      type="radio"
                      name="status"
                      value="draft"
                      checked={postForm.status === 'draft'}
                      onChange={handlePostFormChange}
                    />
                    <span>Draft</span>
                  </label>
                  <label className="create-blogs-radio-label">
                    <input
                      type="radio"
                      name="status"
                      value="published"
                      checked={postForm.status === 'published'}
                      onChange={handlePostFormChange}
                    />
                    <span>Published</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="create-blogs-modal-footer">
              <button
                className="create-blogs-btn-secondary"
                onClick={() => setShowPostModal(false)}
              >
                Cancel
              </button>
              <button
                className="create-blogs-btn-primary"
                onClick={handleSavePost}
                disabled={!postForm.title || !postForm.slug || !postForm.category}
              >
                {editingPost ? 'Update Post' : 'Create Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;